import { X } from 'lucide-react';
import React from 'react';

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';

import {
    isFiltered,
    NO_FILTER,
    type DirectionFilter,
    type PnlFilter,
    type StatusFilter,
    type TapeFilter,
} from './filters';
import { MarketFilterSheet } from './MarketFilterSheet';
import { pseudonymFor } from './pseudonym';

/**
 * Preset sizes rather than a number field. The tape is read by scanning, and a
 * viewer narrowing it wants "the big ones", not $237.50 — a free input would
 * cost a keyboard, a debounce and a validation state to answer a question
 * nobody asks that precisely.
 */
const SIZES = [
    { value: 'any', label: 'Any size', min: null },
    { value: '10', label: '$10+', min: 10 },
    { value: '100', label: '$100+', min: 100 },
    { value: '1000', label: '$1k+', min: 1000 },
] as const;

/** Radix forbids an empty item value, so "no market filter" needs a sentinel. */
const ALL_MARKETS = 'all';

function sizeValue(minMargin: number | null): string {
    return SIZES.find((size) => size.min === minMargin)?.value ?? 'any';
}

/**
 * One labelled group of chips.
 *
 * Radix clears a single group to "", which here means the viewer clicked the
 * chip that was already on — keep it, don't silently fall back to a different
 * filter than the one they can see.
 *
 * `data-narrowed` is what the skin styles on: every group has a chip switched
 * on at all times, and in four of them that chip means "not filtering". Without
 * the distinction an untouched bar lights up as four active filters.
 */
const Group: React.FC<{
    label: string;
    value: string;
    /** the value that means "no filter from this group" */
    unset: string;
    onValueChange: (next: string) => void;
    children: React.ReactNode;
}> = ({ label, value, unset, onValueChange, children }) => (
    <ToggleGroup
        type="single"
        aria-label={label}
        value={value}
        data-narrowed={value !== unset}
        onValueChange={(next) => next && onValueChange(next)}
        size="sm"
        // The group draws nothing. Its default is a bordered, clipping box, and
        // the skin makes every item a 999px pill — a pill inside a 10px box with
        // overflow:hidden has its corners shaved and its fill run into the
        // container's border. Membership is carried by the gap instead: chips sit
        // tight within a group and the groups sit apart.
        // min-h rather than h, and wrapping: the market group holds one chip
        // per market on the tape and runs off the side of a phone otherwise.
        // The short groups never reach the edge, so nothing else moves.
        className="min-h-7 flex-wrap gap-x-0.5 gap-y-1.5 overflow-visible rounded-none border-0 bg-transparent"
    >
        {children}
    </ToggleGroup>
);

/** A chip. Ghost until it is doing something. */
const Chip: React.FC<{ value: string; children: React.ReactNode }> = ({
    value,
    children,
}) => (
    <ToggleGroupItem
        value={value}
        className="border-l-0 bg-transparent px-2.5 hover:bg-accent/40"
    >
        {children}
    </ToggleGroupItem>
);

/**
 * One row of the sheet, or one inline group of the bar.
 *
 * Stacked, each control gets a name, because a phone reads top to bottom and
 * "Both / Long / Short" with no heading is a guess. Inline there is no room for
 * six headings and none are needed — the chips say what they are.
 */
const Field: React.FC<{
    label: string;
    stack: boolean;
    children: React.ReactNode;
}> = ({ label, stack, children }) =>
    stack ? (
        <div className="flex flex-col gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground/70">
                {label}
            </span>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                {children}
            </div>
        </div>
    ) : (
        <>{children}</>
    );

/**
 * What the viewer has narrowed the tape to.
 *
 * Everything here filters the rows the page holds, so the counts beside it have
 * to stay honest — see the `of N` in LiveTrades. The one exception is the
 * trader chip, which is a server-side view and therefore reaches past the
 * window; it is set by clicking a name in the feed rather than chosen here,
 * since a list of every trader would be longer than the tape.
 *
 * `layout` is not cosmetic. Inline, the bar wrapped to four rows on a phone and
 * pushed the first trade a third of the way down a page that exists to show
 * trades; stacked, it is the body of a bottom sheet (see TapeFilterSheet).
 */
export const TapeFilterBar: React.FC<{
    filter: TapeFilter;
    onChange: (next: TapeFilter) => void;
    /** markets present in the loaded rows, uppercased */
    coins: readonly string[];
    layout?: 'bar' | 'stack';
}> = ({ filter, onChange, coins, layout = 'bar' }) => {
    const stack = layout === 'stack';
    const set = <K extends keyof TapeFilter>(key: K, value: TapeFilter[K]) =>
        onChange({ ...filter, [key]: value });

    const fields = (
        <>
            <Field label="Status" stack={stack}>
                <Group
                    label="Trade status"
                    unset="all"
                    value={filter.status}
                    onValueChange={(next) =>
                        set('status', next as StatusFilter)
                    }
                >
                    <Chip value="all">All</Chip>
                    <Chip value="open">Open</Chip>
                    <Chip value="closed">Closed</Chip>
                </Group>
            </Field>

            <Field label="Side" stack={stack}>
                <Group
                    label="Side"
                    unset="all"
                    value={filter.direction}
                    onValueChange={(next) =>
                        set('direction', next as DirectionFilter)
                    }
                >
                    <Chip value="all">Both</Chip>
                    <Chip value="long">Long</Chip>
                    <Chip value="short">Short</Chip>
                </Group>
            </Field>

            <Field label="Result" stack={stack}>
                <Group
                    label="Result"
                    unset="all"
                    value={filter.pnl}
                    onValueChange={(next) => set('pnl', next as PnlFilter)}
                >
                    <Chip value="all">Any</Chip>
                    <Chip value="winners">Winners</Chip>
                    <Chip value="losers">Losers</Chip>
                </Group>
                {/* An open position's figure is derived from the live mark and
                    moves every tick, so a result filter is a filter on settled
                    trades. Said out loud, because rows silently vanishing is the
                    alternative. */}
                {filter.pnl !== 'all' && (
                    <span
                        className={cn(
                            'self-center font-mono text-[9px] uppercase tracking-[0.08em] text-muted-foreground/70',
                            !stack && '-ml-3',
                        )}
                    >
                        settled only
                    </span>
                )}
            </Field>

            <Field label="Size" stack={stack}>
                <Group
                    label="Minimum size"
                    unset="any"
                    value={sizeValue(filter.minMargin)}
                    onValueChange={(next) =>
                        set(
                            'minMargin',
                            SIZES.find((size) => size.value === next)?.min ??
                                null,
                        )
                    }
                >
                    {SIZES.map((size) => (
                        <Chip key={size.value} value={size.value}>
                            {size.label}
                        </Chip>
                    ))}
                </Group>
            </Field>

            <Field label="Market" stack={stack}>
                {stack ? (
                    /* A sheet of its own, not a menu: this row sits near the
                       bottom of the filter sheet, where a dropdown flips upward
                       and covers the sheet that opened it. */
                    <MarketFilterSheet
                        value={filter.coin}
                        onChange={(coin) => set('coin', coin)}
                        coins={coins}
                    />
                ) : (
                    /* Inline there is no room to lay the markets out, and a
                       native <select> renders the OS menu — on a phone it opened
                       detached from the page in the corner of the screen. This
                       is the same Select the terminal's market picker uses. */
                    <Select
                        value={filter.coin ?? ALL_MARKETS}
                        onValueChange={(next) =>
                            set('coin', next === ALL_MARKETS ? null : next)
                        }
                    >
                        <SelectTrigger
                            aria-label="Market"
                            size="sm"
                            className="h-7 rounded-full border-0 bg-transparent px-2.5 font-mono text-xs font-semibold text-muted-foreground hover:bg-accent/40 data-[state=open]:bg-accent/40"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[45dvh] font-mono">
                            <SelectItem value={ALL_MARKETS}>
                                All markets
                            </SelectItem>
                            {coins.map((coin) => (
                                <SelectItem key={coin} value={coin}>
                                    {coin}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </Field>

            {filter.trader && (
                <Field label="Trader" stack={stack}>
                    <span className="flex h-7 items-center gap-1.5 rounded-full bg-[rgba(236,238,228,0.1)] px-2.5 font-mono text-xs font-semibold text-foreground">
                        <span aria-hidden>
                            {pseudonymFor(filter.trader).emoji}
                        </span>
                        <span className="font-sans">
                            {pseudonymFor(filter.trader).name}
                        </span>
                        <button
                            type="button"
                            onClick={() => set('trader', null)}
                            aria-label="Show every trader again"
                            className="text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <X size={11} />
                        </button>
                    </span>
                </Field>
            )}
        </>
    );

    // Stacked, the sheet owns the actions in its footer, so there is no Clear
    // here — see TapeFilterSheet.
    if (stack) {
        return (
            <div className="st-tape-filters flex flex-col gap-4">{fields}</div>
        );
    }

    return (
        <div className="st-tape-filters flex flex-wrap items-center gap-x-6 gap-y-1.5">
            {fields}
            <button
                type="button"
                onClick={() => onChange(NO_FILTER)}
                // Present but inert rather than absent, so the bar does not
                // reflow the moment a filter is chosen or cleared.
                disabled={!isFiltered(filter)}
                className={cn(
                    'h-7 rounded-lg px-2 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors',
                    isFiltered(filter)
                        ? 'text-muted-foreground hover:text-foreground'
                        : 'pointer-events-none opacity-0',
                )}
            >
                Clear
            </button>
        </div>
    );
};

export default TapeFilterBar;
